// UK HSE & British Standards Compliance Data
// Sources: HSE, BSI, DVSA, ISO

import type { RegimeInfo, ComplianceRegimeCode } from '@/types/compliance';

export type { ComplianceRegimeCode } from '@/types/compliance';

export const COMPLIANCE_REGIMES: Record<ComplianceRegimeCode, RegimeInfo> = {
  PUWER: {
    code: 'PUWER',
    name: 'Work Equipment Safety (PUWER)',
    description: 'Provision and Use of Work Equipment Regulations 1998 - requires regular inspection of work equipment to ensure it remains safe throughout its working life.',
    regulatoryBody: 'HSE (Health and Safety Executive)',
    legislationReference: 'PUWER 1998 reg. 6',
    guidanceUrl: 'https://www.hse.gov.uk/work-equipment-machinery/puwer.htm',
    defaultIntervals: [
      'Installation/first-use inspection',
      'Periodic inspections (risk-based, typically 6-12 months)',
      'Post-modification inspection'
    ],
    keyPoints: [
      'Applies to all work equipment including tools, machines, apparatus, ladders, and mobile equipment',
      'Inspection frequency must be determined by risk assessment',
      'Records must be kept until the next inspection',
      'High-risk items require more frequent inspection',
      'Inspections must be carried out by a competent person'
    ]
  },
  LOLER: {
    code: 'LOLER',
    name: 'Lifting Operations (LOLER)',
    description: 'Lifting Operations and Lifting Equipment Regulations 1998 - requires thorough examination of lifting equipment and accessories by a competent person.',
    regulatoryBody: 'HSE (Health and Safety Executive)',
    legislationReference: 'LOLER 1998 reg. 9',
    guidanceUrl: 'https://www.hse.gov.uk/work-equipment-machinery/loler.htm',
    defaultIntervals: [
      '6-month thorough examination (lifting equipment)',
      '12-month thorough examination (lifting accessories)',
      'Pre-use daily checks',
      'Weekly checks (where applicable)'
    ],
    keyPoints: [
      'Applies to all lifting equipment including cranes, hoists, lift trucks, and lifting accessories',
      'Thorough examination must be by a competent person',
      'Written reports must be provided and retained',
      'Defects must be reported and rectified before continued use',
      'Examination intervals may be shorter based on use and environment',
      'See ACOP L113 for detailed guidance'
    ]
  },
  PAT: {
    code: 'PAT',
    name: 'Portable Appliance Testing',
    description: 'Portable appliance testing under Electricity at Work Regulations 1989 - no fixed legal interval; frequency must be based on risk assessment.',
    regulatoryBody: 'HSE (Health and Safety Executive)',
    legislationReference: 'Electricity at Work Regulations 1989',
    guidanceUrl: 'https://www.hse.gov.uk/electricity/faq-portable-appliance-testing.htm',
    defaultIntervals: [
      'Monthly visual checks (user)',
      'Office IT equipment: 24-48 months (suggested)',
      'Extension leads: 6-12 months (suggested)',
      'Site power tools: 3-6 months (suggested)'
    ],
    keyPoints: [
      'No blanket annual PAT testing requirement in law',
      'Frequency must be determined by risk assessment',
      'Consider equipment type, environment, and usage patterns',
      'Visual checks are often sufficient for low-risk equipment',
      'Competent person testing required where formal inspection needed',
      'Keep records of inspections and tests'
    ]
  },
  EICR: {
    code: 'EICR',
    name: 'Fixed Wiring Inspection (EICR)',
    description: 'Electrical Installation Condition Report under BS 7671 (IET Wiring Regulations) - periodic inspection of fixed electrical installations.',
    regulatoryBody: 'BSI (British Standards Institution)',
    legislationReference: 'BS 7671:2018+A2:2022',
    guidanceUrl: 'https://www.intersafe.co.uk/blog/how-often-should-an-eicr-be-carried-out',
    defaultIntervals: [
      '5 years (standard commercial premises)',
      '3 years (high-risk environments)',
      '1 year (critical/hazardous environments)',
      'Shorter intervals if recommended by inspector'
    ],
    keyPoints: [
      'Required for all fixed electrical installations',
      'Must be carried out by qualified electrician',
      'Inspection covers wiring, consumer units, earthing, and bonding',
      'Shorter intervals required for high-risk environments',
      'Inspector may recommend earlier retest if issues found',
      'Certificate valid for period specified by inspector'
    ]
  },
  MOT: {
    code: 'MOT',
    name: 'Vehicle MOT',
    description: 'Ministry of Transport test under Road Traffic Act 1988 - annual roadworthiness test for vehicles over 3 years old.',
    regulatoryBody: 'DVSA (Driver and Vehicle Standards Agency)',
    legislationReference: 'Road Traffic Act 1988',
    guidanceUrl: 'https://www.gov.uk/getting-an-mot',
    defaultIntervals: [
      'First MOT: 3 years after first registration',
      'Subsequent MOTs: Annually',
      'Note: Government consultations on interval changes ongoing'
    ],
    keyPoints: [
      'First MOT due 3 years after first registration (current policy)',
      'Annual MOT required thereafter',
      'Covers safety-critical components: brakes, lights, tyres, steering, etc.',
      'Vehicle cannot be driven without valid MOT (except to pre-booked test)',
      'Servicing is separate from MOT and not statutory',
      'Keep MOT certificates as proof of roadworthiness'
    ]
  },
  FIRE_EXTINGUISHER: {
    code: 'FIRE_EXTINGUISHER',
    name: 'Fire Extinguisher Maintenance',
    description: 'Fire extinguisher servicing under BS 5306-3:2017 - regular maintenance to ensure fire safety equipment remains operational.',
    regulatoryBody: 'BSI (British Standards Institution)',
    legislationReference: 'BS 5306-3:2017',
    guidanceUrl: 'https://www.bafe.org.uk/fire-extinguisher-maintenance',
    defaultIntervals: [
      'Monthly visual checks (Responsible Person)',
      'Annual basic service (competent technician)',
      '5-year extended service (water/foam/powder/wet chemical)',
      '10-year CO₂ extinguisher overhaul'
    ],
    keyPoints: [
      'Responsible Person must conduct monthly visual checks',
      'Annual service by competent technician (BAFE registered recommended)',
      'Extended service every 5 years for most extinguisher types',
      'CO₂ extinguishers require 10-year overhaul',
      'Service records must be maintained',
      'Extinguishers must be easily accessible and clearly signed'
    ]
  },
  CALIBRATION: {
    code: 'CALIBRATION',
    name: 'Instrument Calibration',
    description: 'Calibration of measuring and safety devices under ISO 55001 - ensures accuracy and reliability of critical instruments.',
    regulatoryBody: 'ISO (International Organisation for Standardisation)',
    legislationReference: 'ISO 55001:2024',
    guidanceUrl: 'https://www.iso.org/standard/83053.html',
    defaultIntervals: [
      'Annual calibration (typical for most instruments)',
      'Interval varies by instrument type and criticality',
      'More frequent for high-precision or safety-critical devices'
    ],
    keyPoints: [
      'Calibration intervals must be risk-based',
      'Consider instrument type, usage, and criticality',
      'Calibration certificates must be retained',
      'Out-of-tolerance instruments must be marked as non-compliant',
      'Traceable to national/international standards',
      'Examples: torque wrenches, gas detectors, scales, pressure gauges'
    ]
  },
  ISO_55001: {
    code: 'ISO_55001',
    name: 'Asset Management System (ISO 55001)',
    description: 'ISO 55001:2024 Asset Management - systematic approach to managing physical assets throughout their lifecycle.',
    regulatoryBody: 'ISO (International Organisation for Standardisation)',
    legislationReference: 'ISO 55001:2024',
    guidanceUrl: 'https://www.iso.org/standard/83053.html',
    defaultIntervals: [
      'Continuous lifecycle management',
      'Regular performance reviews',
      'Annual management review',
      'Periodic audits (internal and external)'
    ],
    keyPoints: [
      'Focus on lifecycle management and value realization',
      'Risk-based decision making',
      'Balance cost, performance, and risk',
      'Documented asset information and decision criteria',
      'Continual improvement approach',
      'Alignment with organizational objectives'
    ]
  },
  ISO_45001: {
    code: 'ISO_45001',
    name: 'Occupational Health & Safety (ISO 45001)',
    description: 'ISO 45001:2018 OH&S Management - systematic approach to managing occupational health and safety risks.',
    regulatoryBody: 'ISO (International Organisation for Standardisation)',
    legislationReference: 'ISO 45001:2018',
    guidanceUrl: 'https://www.iso.org/standard/63787.html',
    defaultIntervals: [
      'Continuous hazard identification',
      'Regular risk assessments',
      'Periodic OH&S performance monitoring',
      'Annual management review'
    ],
    keyPoints: [
      'Plan-Do-Check-Act (PDCA) cycle',
      'Hazard identification and risk assessment',
      'Worker participation and consultation',
      'Compliance with legal and other requirements',
      'Continual improvement of OH&S performance',
      'Integration with other management systems'
    ]
  }
};

// Status badge configurations
export const STATUS_CONFIG = {
  compliant: {
    label: 'Compliant',
    variant: 'default' as const,
    className: 'bg-green-500 hover:bg-green-600 text-white',
    icon: 'CheckCircle'
  },
  'due-soon': {
    label: 'Due Soon',
    variant: 'default' as const,
    className: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    icon: 'Clock'
  },
  overdue: {
    label: 'Overdue',
    variant: 'destructive' as const,
    className: 'bg-red-500 hover:bg-red-600 text-white',
    icon: 'AlertCircle'
  },
  unknown: {
    label: 'Unknown',
    variant: 'secondary' as const,
    className: 'bg-gray-500 hover:bg-gray-600 text-white',
    icon: 'HelpCircle'
  }
};

// Risk band configurations
export const RISK_BAND_CONFIG = {
  LOW: {
    label: 'Low',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    color: '#3b82f6'
  },
  MEDIUM: {
    label: 'Medium',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    color: '#f59e0b'
  },
  HIGH: {
    label: 'High',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    color: '#f97316'
  },
  CRITICAL: {
    label: 'Critical',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    color: '#ef4444'
  }
};

// Defect severity configurations
export const SEVERITY_CONFIG = {
  CRITICAL: {
    label: 'Critical',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-500',
    icon: 'AlertTriangle'
  },
  HIGH: {
    label: 'High',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border-orange-500',
    icon: 'AlertCircle'
  },
  MEDIUM: {
    label: 'Medium',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-500',
    icon: 'Info'
  },
  LOW: {
    label: 'Low',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-blue-500',
    icon: 'Info'
  },
  ADVISORY: {
    label: 'Advisory',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 border-gray-500',
    icon: 'Info'
  }
};

// Event result configurations
export const RESULT_CONFIG = {
  PASS: {
    label: 'Pass',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    icon: 'CheckCircle'
  },
  FAIL: {
    label: 'Fail',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    icon: 'XCircle'
  },
  ADVISORY: {
    label: 'Advisory',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    icon: 'AlertCircle'
  },
  CONDITIONAL_PASS: {
    label: 'Conditional Pass',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    icon: 'AlertTriangle'
  }
};

// Helper functions
export function getRegimeInfo(code: string | ComplianceRegimeCode): RegimeInfo {
  return COMPLIANCE_REGIMES[code as ComplianceRegimeCode];
}

export function getStatusConfig(status: string): typeof STATUS_CONFIG[keyof typeof STATUS_CONFIG] {
  return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.unknown;
}

export function getRiskBandConfig(riskBand: string): typeof RISK_BAND_CONFIG[keyof typeof RISK_BAND_CONFIG] {
  return RISK_BAND_CONFIG[riskBand as keyof typeof RISK_BAND_CONFIG] || RISK_BAND_CONFIG.MEDIUM;
}

export function getSeverityConfig(severity: string): typeof SEVERITY_CONFIG[keyof typeof SEVERITY_CONFIG] {
  return SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.MEDIUM;
}

export function getResultConfig(result: string): typeof RESULT_CONFIG[keyof typeof RESULT_CONFIG] {
  return RESULT_CONFIG[result as keyof typeof RESULT_CONFIG] || RESULT_CONFIG.PASS;
}

export function calculateDaysUntilDue(nextDueDate: string | undefined): number | undefined {
  if (!nextDueDate) return undefined;
  const due = new Date(nextDueDate);
  const now = new Date();
  const diffTime = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function calculateComplianceStatus(daysUntilDue: number | undefined): string {
  if (daysUntilDue === undefined) return 'unknown';
  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= 30) return 'due-soon';
  return 'compliant';
}

export function formatInterval(intervalDays: number | undefined, intervalUnit?: string, intervalValue?: number): string {
  if (intervalUnit && intervalValue) {
    return `${intervalValue} ${intervalUnit.toLowerCase()}`;
  }
  if (intervalDays) {
    if (intervalDays % 365 === 0) {
      const years = intervalDays / 365;
      return `${years} year${years > 1 ? 's' : ''}`;
    }
    if (intervalDays % 30 === 0) {
      const months = intervalDays / 30;
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    return `${intervalDays} days`;
  }
  return 'Not set';
}
